import React, { useState } from 'react';
import { View, Text, ScrollView, Pressable } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import * as Haptics from 'expo-haptics';
import {
  Sparkles,
  TrendingUp,
  ArrowUp,
  CheckCircle,
  Circle,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Users,
  Target,
  MessageCircle,
  Calendar,
  MapPin,
  Briefcase,
  DollarSign,
  Bot,
  Trash2,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp, FadeIn, Layout } from 'react-native-reanimated';
import type { InvestorMatch } from '@/lib/state/inverio-store';
import { useFlag } from '@/lib/feature-flags';
import { useProfileStore } from '@/lib/state/profile-store';
import { useCreateConversation, isRelationshipDenied } from '@/lib/supabase';
import { useAlert } from '@/components/AlertDialog';
import { handleError } from '@/lib/errors';
import { useScoreStore } from '@/lib/state/score-store';
import type { AIRecommendation } from '@/lib/scoring/types';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { formatCurrency, getScoreColor } from '@/lib/utils/format';
import { track, useTrackScreen } from '@/lib/analytics';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { AppLayout } from '@/components/AppLayout';
import { SectionDivider } from '@/components/SectionDivider';
import { TierGate, UpgradePrompt } from '@/components/subscription';
import AnimatedPressable from '@/components/AnimatedPressable';

// Standard shadow styles
const cardShadow = {
  shadowColor: '#000',
  shadowOpacity: 0.05,
  shadowRadius: 8,
  shadowOffset: { width: 0, height: 2 },
  elevation: 2,
};

const getPriorityConfig = (colors: ReturnType<typeof useThemeColors>) => ({
  high: { color: colors.error, bg: colors.red.light, label: 'High Priority' },
  medium: { color: colors.warning, bg: colors.amber.light, label: 'Medium Priority' },
  low: { color: colors.success, bg: colors.traffic.greenLight, label: 'Low Priority' },
});

const getMatchStatusConfig = (colors: ReturnType<typeof useThemeColors>) => ({
  new: { color: colors.primary[600], bg: colors.primary[50], label: 'New Match' },
  contacted: { color: colors.warning, bg: colors.amber.light, label: 'Contacted' },
  meeting_scheduled: { color: colors.success, bg: colors.traffic.greenLight, label: 'Meeting Set' },
  passed: { color: colors.text.tertiary, bg: colors.surface.tertiary, label: 'Passed' },
});

export default function RecommendationsScreen() {
  useTrackScreen('Recommendations');
  const router = useRouter();

  // `ai_coach` is seeded OFF. This screen is linked unconditionally from the web sidebar,
  // which itself refuses to list the advisor for exactly this reason, so the two CTAs below
  // were the only place a paused product was still being sold. useFlag (render), not
  // useFlagRouteAccess (navigation) — see src/lib/feature-flags/route-access.ts.
  const aiCoachEnabled = useFlag('ai_coach', { default: false });
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const priorityConfig = getPriorityConfig(colors);
  const matchStatusConfig = getMatchStatusConfig(colors);
  const recommendations = useScoreStore((s) => s.aiRecommendations);
  const investorMatches = useScoreStore((s) => s.investorMatches);
  const createConversation = useCreateConversation();
  const { error: showError } = useAlert();
  const startupProfile = useProfileStore((s) => s.startupProfile);
  const removeAIRecommendation = useScoreStore((s) => s.removeAIRecommendation);
  const [expandedRec, setExpandedRec] = useState<string | null>(null);
  const [expandedMatch, setExpandedMatch] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'recommendations' | 'matches'>('recommendations');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'scoring' | 'insights'>('all');

  const filteredRecommendations = recommendations.filter((rec) => {
    if (sourceFilter === 'all') return true;
    return (rec.source ?? 'scoring') === sourceFilter;
  });

  const scoringRecommendations = recommendations.filter((r) => (r.source ?? 'scoring') === 'scoring');
  const insightsCount = recommendations.filter((r) => r.source === 'insights').length;

  // Feature gating - investor matching requires Premium
  const hasInvestorMatching = useSubscriptionStore((s) => s.hasFeature('automatic_investor_matching'));

  const handleRequestIntroduction = async (match: InvestorMatch) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);

    if (!startupProfile?.id) {
      showError('Complete Your Profile', 'Set up your startup profile before requesting introductions.');
      return;
    }

    // Conversation creation is RLS relationship-gated — a founder can only open
    // a thread with an investor who has already engaged. Surface an honest
    // message on denial rather than faking a conversation.
    try {
      await createConversation.mutateAsync({
        startupId: startupProfile.id,
        investorId: match.investor.id,
      });
      router.push('/(startup)/startup-messages');
    } catch (err) {
      if (isRelationshipDenied(err)) {
        showError(
          'Not Available Yet',
          "You can message an investor once they've engaged with your startup — for example, after they've requested access to your data room.",
        );
        return;
      }
      const { message, isCancelled } = handleError(err, { context: 'Recommendations.handleRequestIntroduction' });
      if (!isCancelled) showError('Could Not Start Conversation', message);
    }
  };

  const potentialScoreGain = scoringRecommendations.reduce(
    (sum, r) => sum + (r.potentialScore - r.currentScore),
    0
  );

  return (
    <AppLayout title="Recommendations">
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
          <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
            {/* Header - Only show on mobile */}
            {phoneShell && (
              <Animated.View
                entering={FadeInDown.delay(100).duration(500)}
                className="px-5 pt-6 pb-6"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <View className="flex-row items-center gap-3">
                  {/* Pushed within the tabs navigator with `href: null` — the
                      only way out was the tab bar. Same inline back control
                      every other custom mobile header in this app already
                      uses (see settings.tsx), guarded so it never renders a
                      dead control when there is no history to go back to. */}
                  {router.canGoBack() && (
                    <AnimatedPressable
                      onPress={() => router.back()}
                      accessibilityRole="button"
                      accessibilityLabel="Go back"
                      className="w-11 h-11 rounded-full items-center justify-center"
                      style={{ backgroundColor: colors.surface.secondary }}
                    >
                      <ChevronLeft size={20} color={colors.text.primary} />
                    </AnimatedPressable>
                  )}
                  <View className="flex-1">
                    <View className="flex-row items-center gap-2">
                      <Sparkles size={20} color={colors.warning} />
                      <Text style={{ color: colors.text.secondary }} className="text-sm">AI-Powered</Text>
                    </View>
                    <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mt-1" numberOfLines={1}>Recommendations</Text>
                  </View>
                </View>
              </Animated.View>
            )}

          {/* AI Advisor CTA — hidden entirely while ai_coach is off. */}
          {aiCoachEnabled && (
          <Animated.View
            entering={FadeInUp.delay(150).duration(500)}
            className="px-5 mb-4"
          >
            <AnimatedPressable
              onPress={() => {
                Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                router.push('/(startup)/insights');
              }}
              className="flex-row items-center justify-between px-4 py-3.5 rounded-2xl"
              style={{
                backgroundColor: colors.primary[50],
                borderWidth: 1,
                borderColor: colors.primary[200],
              }}
            >
              <View className="flex-row items-center gap-3">
                <View
                  className="w-10 h-10 rounded-xl items-center justify-center"
                  style={{ backgroundColor: colors.primary[600] }}
                >
                  <Bot size={20} color="white" />
                </View>
                <View>
                  <Text style={{ color: colors.text.primary }} className="font-semibold text-sm" numberOfLines={1}>AI Advisor</Text>
                  <Text style={{ color: colors.text.secondary }} className="text-xs">Chat with AI about your investment readiness</Text>
                </View>
              </View>
              <View
                className="px-3 py-1.5 rounded-lg"
                style={{ backgroundColor: colors.primary[600] }}
              >
                <Text style={{ color: colors.text.inverse }} className="text-xs font-semibold">Chat</Text>
              </View>
            </AnimatedPressable>
          </Animated.View>
          )}

          {/* Tab Selector */}
          <Animated.View
            entering={FadeInUp.delay(200).duration(500)}
            className="px-5 mb-6"
          >
            <View className="flex-row rounded-2xl p-1.5" style={{ backgroundColor: colors.surface.tertiary }}>
              <Pressable
                onPress={() => setActiveTab('recommendations')}
                className={`flex-1 py-3 rounded-xl ${activeTab === 'recommendations' ? '' : ''}`}
                style={activeTab === 'recommendations' ? { backgroundColor: colors.primary[600] } : {}}
              >
                <Text
                  className="text-center font-semibold"
                  style={{ color: activeTab === 'recommendations' ? colors.text.inverse : colors.text.tertiary }}
                >
                  Recommendations
                </Text>
              </Pressable>
              <Pressable
                onPress={() => setActiveTab('matches')}
                className={`flex-1 py-3 rounded-xl overflow-hidden ${activeTab === 'matches' ? '' : ''}`}
                style={activeTab === 'matches' ? { backgroundColor: colors.primary[600] } : {}}
              >
                <Text
                  className="text-center font-semibold"
                  style={{ color: activeTab === 'matches' ? colors.text.inverse : colors.text.tertiary }}
                >
                  Investor Matches
                </Text>
              </Pressable>
            </View>
          </Animated.View>

          {activeTab === 'recommendations' ? (
            <>
              {/* Score Potential Card */}
              <Animated.View
                entering={FadeInUp.delay(300).duration(500)}
                className="mx-5 mb-6"
              >
                <LinearGradient
                  colors={[colors.primary[500], colors.primary[600]]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={{ borderRadius: 20, padding: 20 }}
                >
                  <View className="flex-row items-center justify-between">
                    <View>
                      <Text className="text-sm mb-1" style={{ color: `${colors.primary[50]}CC` }}>
                        Potential Score Increase
                      </Text>
                      <View className="flex-row items-center gap-2">
                        <ArrowUp size={24} color={colors.surface.primary} />
                        <Text className="text-4xl font-bold" style={{ color: colors.text.inverse }}>
                          +{potentialScoreGain}
                        </Text>
                        <Text className="text-lg" style={{ color: `${colors.primary[50]}99` }}>pts</Text>
                      </View>
                      <Text className="text-xs mt-2" style={{ color: `${colors.primary[50]}99` }}>
                        Complete all {scoringRecommendations.length} score recommendations
                      </Text>
                    </View>
                    <View className="w-16 h-16 rounded-full items-center justify-center" style={{ backgroundColor: `${colors.surface.primary}20` }}>
                      <TrendingUp size={32} color={colors.surface.primary} />
                    </View>
                  </View>
                </LinearGradient>
              </Animated.View>

              {/* Filter Chips */}
              {insightsCount > 0 && (
                <Animated.View
                  entering={FadeInUp.delay(350).duration(400)}
                  className="flex-row gap-2 px-5 mb-4"
                >
                  {(['all', 'scoring', 'insights'] as const).map((filter) => {
                    const labels = { all: 'All', scoring: 'Score Improvements', insights: 'AI Advisor' };
                    const isActive = sourceFilter === filter;
                    const count = filter === 'all'
                      ? recommendations.length
                      : filter === 'scoring'
                        ? scoringRecommendations.length
                        : insightsCount;

                    return (
                      <Pressable
                        key={filter}
                        onPress={() => setSourceFilter(filter)}
                        className="px-3 py-1.5 rounded-full flex-row items-center gap-1.5"
                        style={{
                          backgroundColor: isActive ? colors.primary[600] : colors.surface.tertiary,
                        }}
                      >
                        <Text
                          className="text-xs font-medium"
                          style={{ color: isActive ? colors.text.inverse : colors.text.secondary }}
                        >
                          {labels[filter]}
                        </Text>
                        <View
                          className="w-4 h-4 rounded-full items-center justify-center"
                          style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : colors.surface.primary }}
                        >
                          <Text className="text-[10px]" style={{ color: isActive ? colors.text.inverse : colors.text.tertiary }}>
                            {count}
                          </Text>
                        </View>
                      </Pressable>
                    );
                  })}
                </Animated.View>
              )}

              {/* Section Divider - Action Items */}
              <View className="px-5">
                <SectionDivider
                  label={sourceFilter === 'insights' ? 'AI Advisor Items' : 'Action Items'}
                  marginTop={8}
                  marginBottom={12}
                />
              </View>

              {/* Recommendations List */}
              <Animated.View
                entering={FadeInUp.delay(400).duration(500)}
                className="px-5 pb-8"
              >
                {filteredRecommendations.length === 0 && sourceFilter === 'insights' ? (
                  <View className="items-center py-12 px-6">
                    <View
                      className="w-14 h-14 rounded-2xl items-center justify-center mb-4"
                      style={{ backgroundColor: colors.amber?.light || '#FEF3C7' }}
                    >
                      <Bot size={28} color={colors.warning} />
                    </View>
                    <Text style={{ color: colors.text.secondary }} className="text-sm text-center leading-relaxed">
                      {aiCoachEnabled
                        ? 'No AI Advisor items yet. Chat with the AI Advisor to get personalized action items.'
                        : 'No AI Advisor items yet.'}
                    </Text>
                    {aiCoachEnabled && (
                    <AnimatedPressable
                      onPress={() => {
                        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                        router.push('/(startup)/insights');
                      }}
                      className="mt-4 px-4 py-2.5 rounded-xl"
                      style={{ backgroundColor: colors.primary[600] }}
                    >
                      <Text style={{ color: colors.text.inverse }} className="text-sm font-semibold">Open AI Advisor</Text>
                    </AnimatedPressable>
                    )}
                  </View>
                ) : (
                <View className="gap-3">
                  {filteredRecommendations.map((rec) => {
                    const priority = priorityConfig[rec.priority];
                    const isExpanded = expandedRec === rec.id;
                    const isInsights = rec.source === 'insights';

                    return (
                      <Animated.View
                        key={rec.id}
                        layout={Layout.springify()}
                      >
                        <AnimatedPressable
                          onPress={() => setExpandedRec(isExpanded ? null : rec.id)}
                          className="rounded-2xl overflow-hidden"
                          style={{
                            backgroundColor: colors.surface.primary,
                            ...(isInsights ? { borderLeftWidth: 3, borderLeftColor: colors.warning } : {}),
                          }}
                        >
                          <View className="p-4">
                            <View className="flex-row items-start justify-between mb-3">
                              <View className="flex-1">
                                <View className="flex-row items-center gap-2 mb-2">
                                  <View
                                    className="self-start px-2 py-1 rounded-full"
                                    style={{ backgroundColor: priority.bg }}
                                  >
                                    <Text
                                      className="text-xs font-semibold"
                                      style={{ color: priority.color }}
                                    >
                                      {priority.label}
                                    </Text>
                                  </View>
                                  {isInsights && (
                                    <View
                                      className="flex-row items-center gap-1 px-2 py-1 rounded-full"
                                      style={{ backgroundColor: colors.amber?.light || '#FEF3C7' }}
                                    >
                                      <Bot size={12} color={colors.warning} />
                                      <Text className="text-xs font-medium" style={{ color: colors.warning }}>
                                        AI Advisor
                                      </Text>
                                    </View>
                                  )}
                                </View>
                                <Text style={{ color: colors.text.primary }} className="font-semibold text-base">
                                  {rec.area}
                                </Text>
                              </View>
                              <View className="items-end">
                                {isInsights ? (
                                  <View className="h-5" />
                                ) : (
                                  <View className="flex-row items-center gap-1">
                                    <Text style={{ color: colors.text.tertiary }} className="text-sm">
                                      {rec.currentScore}
                                    </Text>
                                    <ArrowUp size={14} color={colors.success} />
                                    <Text style={{ color: colors.success }} className="text-sm font-bold">
                                      {rec.potentialScore}
                                    </Text>
                                  </View>
                                )}
                                {isExpanded ? (
                                  <ChevronUp size={20} color={colors.text.tertiary} />
                                ) : (
                                  <ChevronDown size={20} color={colors.text.tertiary} />
                                )}
                              </View>
                            </View>
                            <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
                              {rec.suggestion}
                            </Text>
                          </View>

                          {isExpanded && (
                            <Animated.View
                              entering={FadeIn.duration(200)}
                              className="p-4"
                              style={{ backgroundColor: colors.surface.secondary, borderTopWidth: 1, borderTopColor: colors.border.light }}
                            >
                              <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-3">
                                Action Items
                              </Text>
                              {rec.actionItems.map((item, idx) => (
                                <View
                                  key={idx}
                                  className="flex-row items-start gap-3 mb-2"
                                >
                                  <Circle size={16} color={colors.text.tertiary} />
                                  <Text style={{ color: colors.text.secondary }} className="text-sm flex-1">
                                    {item}
                                  </Text>
                                </View>
                              ))}
                              {isInsights && (
                                <AnimatedPressable
                                  onPress={() => {
                                    removeAIRecommendation(rec.id);
                                    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                                    setExpandedRec(null);
                                  }}
                                  className="flex-row items-center gap-2 mt-3 pt-3"
                                  style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}
                                >
                                  <Trash2 size={14} color={colors.error} />
                                  <Text style={{ color: colors.error }} className="text-xs">Remove from recommendations</Text>
                                </AnimatedPressable>
                              )}
                            </Animated.View>
                          )}
                        </AnimatedPressable>
                      </Animated.View>
                    );
                  })}
                </View>
                )}
              </Animated.View>
              </>
            ) : hasInvestorMatching ? (
              <>
                {/* Matches Summary */}
                <Animated.View
                  entering={FadeInUp.delay(300).duration(500)}
                  className="mx-5 mb-6"
                >
                  <LinearGradient
                    colors={[colors.primary[500], colors.primary[600]]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={{ borderRadius: 20, padding: 20 }}
                  >
                    <View className="flex-row items-center justify-between">
                      <View>
                        <Text className="text-sm mb-1" style={{ color: `${colors.primary[50]}CC` }}>
                          AI Matched Investors
                        </Text>
                        <Text className="text-4xl font-bold" style={{ color: colors.text.inverse }}>
                          {investorMatches.length}
                        </Text>
                        <Text className="text-xs mt-2" style={{ color: `${colors.primary[50]}99` }}>
                          Based on your profile and criteria
                        </Text>
                      </View>
                      <View className="w-16 h-16 rounded-full items-center justify-center" style={{ backgroundColor: `${colors.surface.primary}20` }}>
                        <Users size={32} color={colors.surface.primary} />
                      </View>
                    </View>
                  </LinearGradient>
                </Animated.View>

                {/* Section Divider - Top Matches */}
                <View className="px-5">
                  <SectionDivider label="Top Matches" marginTop={8} marginBottom={12} />
                </View>

                {/* Investor Matches List */}
                <Animated.View
                  entering={FadeInUp.delay(400).duration(500)}
                  className="px-5 pb-8"
                >
                  <View className="gap-4">
                    {investorMatches.map((match) => {
                      const status = matchStatusConfig[match.status];
                      const isExpanded = expandedMatch === match.id;

                      return (
                        <Animated.View
                          key={match.id}
                          layout={Layout.springify()}
                        >
                          <AnimatedPressable
                            onPress={() => setExpandedMatch(isExpanded ? null : match.id)}
                            className="rounded-2xl overflow-hidden"
                            style={{ backgroundColor: colors.surface.primary }}
                          >
                            <View className="p-4">
                              <View className="flex-row items-start gap-3">
                                <LinearGradient
                                  colors={[colors.primary[400], colors.primary[600]]}
                                  style={{ width: 56, height: 56, borderRadius: 12, alignItems: 'center', justifyContent: 'center' }}
                                >
                                  <Text className="text-xl font-bold" style={{ color: colors.text.inverse }}>
                                    {match.investorName.split(' ').map((n: string) => n[0]).join('')}
                                  </Text>
                                </LinearGradient>
                                <View className="flex-1">
                                  <View className="flex-row items-center justify-between">
                                    <Text style={{ color: colors.text.primary }} className="font-semibold text-base">
                                      {match.investorName}
                                    </Text>
                                    <View
                                      className="px-2 py-1 rounded-full"
                                      style={{ backgroundColor: status.bg }}
                                    >
                                      <Text
                                        className="text-xs font-medium"
                                        style={{ color: status.color }}
                                      >
                                        {status.label}
                                      </Text>
                                    </View>
                                  </View>
                                  {match.investor.firm && (
                                    <Text style={{ color: colors.text.secondary }} className="text-sm">
                                      {match.investor.firm}
                                    </Text>
                                  )}
                                  <View className="flex-row items-center gap-4 mt-2">
                                    <View className="flex-row items-center gap-1">
                                      <Target size={14} color={colors.success} />
                                      <Text style={{ color: colors.success }} className="text-sm font-bold">
                                        {match.matchScore}% match
                                      </Text>
                                    </View>
                                    <View className="flex-row items-center gap-1">
                                      <MapPin size={14} color={colors.text.tertiary} />
                                      <Text style={{ color: colors.text.tertiary }} className="text-xs">
                                        {match.investor.location.split(',')[0]}
                                      </Text>
                                    </View>
                                  </View>
                                </View>
                              </View>

                              {/* Match Reasons Preview */}
                              <View className="flex-row flex-wrap gap-2 mt-3">
                                {match.matchReasons.slice(0, 2).map((reason, idx) => (
                                  <View
                                    key={idx}
                                    className="px-2 py-1 rounded-lg"
                                    style={{ backgroundColor: colors.surface.tertiary }}
                                  >
                                    <Text style={{ color: colors.text.secondary }} className="text-xs">{reason}</Text>
                                  </View>
                                ))}
                                {match.matchReasons.length > 2 && (
                                  <View className="px-2 py-1 rounded-lg" style={{ backgroundColor: colors.surface.tertiary }}>
                                    <Text style={{ color: colors.text.tertiary }} className="text-xs">
                                      +{match.matchReasons.length - 2} more
                                    </Text>
                                  </View>
                                )}
                              </View>
                            </View>

                            {isExpanded && (
                              <Animated.View
                                entering={FadeIn.duration(200)}
                                className="p-4"
                                style={{ backgroundColor: colors.surface.secondary, borderTopWidth: 1, borderTopColor: colors.border.light }}
                              >
                                <View className="gap-3">
                                  <View className="flex-row items-center gap-2">
                                    <Briefcase size={16} color={colors.text.tertiary} />
                                    <Text style={{ color: colors.text.secondary }} className="text-sm">
                                      Focus: {match.investor.investmentFocus.join(', ')}
                                    </Text>
                                  </View>
                                  <View className="flex-row items-center gap-2">
                                    <DollarSign size={16} color={colors.text.tertiary} />
                                    <Text style={{ color: colors.text.secondary }} className="text-sm">
                                      Ticket: {formatCurrency(match.investor.ticketSize.min)} - {formatCurrency(match.investor.ticketSize.max)}
                                    </Text>
                                  </View>
                                  <View className="flex-row items-center gap-2">
                                    <Target size={16} color={colors.text.tertiary} />
                                    <Text style={{ color: colors.text.secondary }} className="text-sm">
                                      Stage: {match.investor.stagePreference.join(', ')}
                                    </Text>
                                  </View>

                                  <Text style={{ color: colors.text.tertiary }} className="text-xs font-medium mt-2 mb-1">
                                    Why this match:
                                  </Text>
                                  {match.matchReasons.map((reason, idx) => (
                                    <View key={idx} className="flex-row items-center gap-2">
                                      <CheckCircle size={14} color={colors.success} />
                                      <Text style={{ color: colors.text.secondary }} className="text-sm">{reason}</Text>
                                    </View>
                                  ))}

                                  <AnimatedPressable
                                    onPress={() => handleRequestIntroduction(match)}
                                    className="rounded-xl py-3 mt-3"
                                    style={{ backgroundColor: colors.primary[600] }}
                                  >
                                    <View className="flex-row items-center justify-center gap-2">
                                      <MessageCircle size={18} color="white" />
                                      <Text className="font-semibold" style={{ color: colors.text.inverse }}>
                                        Request Introduction
                                      </Text>
                                    </View>
                                  </AnimatedPressable>
                                </View>
                              </Animated.View>
                            )}
                          </AnimatedPressable>
                        </Animated.View>
                      );
                    })}
                  </View>
                </Animated.View>
              </>
            ) : (
              // Upgrade prompt for non-Premium users
              <View className="flex-1 px-5 py-8">
                <UpgradePrompt
                  targetTier="premium"
                  variant="fullscreen"
                  headline="Unlock Investor Matching"
                  lockedTitle="Investor matching"
                  maxFeatures={4}
                />
              </View>
            )}
          </ScrollView>
        </SafeAreaView>
      </View>
    </AppLayout>
  );
}
